import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { formatCurrency } from '@/utils/currencyFormat';
import { TrendingUp, Wallet, ShieldAlert, ArrowUpRight, ArrowDownRight, Eye, EyeOff } from 'lucide-react';

const MobileBalanceCard = ({ 
  patrimonioNeto,
  liquidAssets = 0,
  savingsAssets = 0,
  totalDebt = 0,
  creditCardDebt = 0,
  loanDebt = 0,
  monthlyFlow = 0,
  periodIncome = 0,
  periodExpense = 0,
  globalHideBalance = false,
  // Compatibilidad hacia atrás
  balance,
  available,
  onAddClick 
}) => {
  const [viewMode, setViewMode] = useState('period'); // 'period' (default) | 'networth'
  const [hideCardBalance, setHideCardBalance] = useState(globalHideBalance);

  useEffect(() => {
    setHideCardBalance(globalHideBalance);
  }, [globalHideBalance]);

  const toggleHideCardBalance = () => setHideCardBalance(prev => !prev);

  const totalAssets = liquidAssets + savingsAssets;
  const realNetWorth = patrimonioNeto !== undefined ? patrimonioNeto : (totalAssets - (totalDebt || 0));
  const effectiveMonthlyFlow = monthlyFlow !== undefined ? monthlyFlow : (balance || 0);

  const isPeriodPositive = effectiveMonthlyFlow >= 0;
  const isNetWorthPositive = realNetWorth >= 0;
  const isCurrentPositive = viewMode === 'period' ? isPeriodPositive : isNetWorthPositive;

  return (
    <Card className="p-5 bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 border border-slate-800 shadow-2xl relative overflow-hidden group rounded-[2rem] text-white">
      {/* Background Decor */}
      <div className={`absolute -right-6 -top-6 w-36 h-36 rounded-full blur-3xl pointer-events-none transition-all duration-700 ${
        isCurrentPositive ? 'bg-emerald-500/15 group-hover:bg-emerald-500/25' : 'bg-rose-500/15 group-hover:bg-rose-500/25'
      }`} />

      <div className="relative z-10 space-y-4">
        {/* Selector tipo Pill: Flujo del Período vs Patrimonio Real */}
        <div className="flex items-center justify-between gap-2">
          <div className="bg-slate-800/90 p-1 rounded-xl flex items-center border border-slate-700/60 shrink-0">
            <button
              type="button"
              onClick={() => setViewMode('period')}
              className={`px-3 py-1 rounded-lg text-[10px] font-extrabold transition-all ${
                viewMode === 'period'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Flujo del Período
            </button>
            <button
              type="button"
              onClick={() => setViewMode('networth')}
              className={`px-3 py-1 rounded-lg text-[10px] font-extrabold transition-all ${
                viewMode === 'networth'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Patrimonio Real
            </button>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleHideCardBalance();
              }}
              className="p-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 hover:text-white transition-colors"
              title={hideCardBalance ? "Mostrar cantidades" : "Ocultar cantidades"}
              aria-label="Ocultar o mostrar cantidades"
            >
              {hideCardBalance ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>

            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider shadow-sm border ${
              isCurrentPositive 
                ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' 
                : 'bg-rose-500/15 text-rose-400 border-rose-500/30 animate-pulse'
            }`}>
              {isCurrentPositive ? <TrendingUp className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
              {viewMode === 'period'
                ? (isPeriodPositive ? 'Superávit' : 'Déficit')
                : (isNetWorthPositive ? 'Positivo' : 'Déficit Total')}
            </span>
          </div>
        </div>

        {/* Monto Principal */}
        <div>
          <div className="flex items-center gap-1.5 mb-1 text-slate-400">
            <Wallet className="w-3.5 h-3.5 text-indigo-400" />
            <span className="text-[10px] font-bold uppercase tracking-wider">
              {viewMode === 'period' ? 'Resultado Neto del Período' : 'Patrimonio Neto Real'}
            </span>
          </div>

          <p className={`text-3xl sm:text-4xl font-black tracking-tight ${
            isCurrentPositive ? 'text-white' : 'text-rose-400'
          }`}>
            {hideCardBalance 
              ? '••••••' 
              : (viewMode === 'period'
                  ? (effectiveMonthlyFlow >= 0 ? `+${formatCurrency(effectiveMonthlyFlow)}` : formatCurrency(effectiveMonthlyFlow))
                  : formatCurrency(realNetWorth))}
          </p>

          <p className="text-[10px] font-medium text-slate-400 mt-0.5">
            {viewMode === 'period'
              ? (isPeriodPositive 
                  ? 'Tus ingresos superaron tus gastos en el período seleccionado.' 
                  : 'Tus gastos excedieron tus ingresos en este período.')
              : (isNetWorthPositive 
                  ? 'Tus activos totales cubren la totalidad de tus deudas.' 
                  : 'Tus deudas superan el total de tus activos disponibles.')}
          </p>
        </div>

        {/* Desglose Secundario Dinámico según ViewMode */}
        {viewMode === 'period' ? (
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-800/80">
            <div className="bg-slate-800/50 backdrop-blur-sm p-2.5 rounded-xl border border-slate-700/40">
              <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400 uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                Ingresos Período
              </div>
              <p className="text-sm font-black text-emerald-400 mt-0.5 truncate">
                {hideCardBalance ? '••••••' : formatCurrency(periodIncome)}
              </p>
              <span className="text-[9px] text-slate-400">Entradas del período</span>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm p-2.5 rounded-xl border border-slate-700/40">
              <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400 uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
                Gastos Período
              </div>
              <p className="text-sm font-black text-rose-400 mt-0.5 truncate">
                {hideCardBalance ? '••••••' : formatCurrency(periodExpense)}
              </p>
              <span className="text-[9px] text-slate-400">Salidas del período</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-800/80">
            <div className="bg-slate-800/50 backdrop-blur-sm p-2.5 rounded-xl border border-slate-700/40">
              <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400 uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                Activos Totales
              </div>
              <p className="text-sm font-black text-emerald-400 mt-0.5 truncate">
                {hideCardBalance ? '••••••' : formatCurrency(totalAssets)}
              </p>
              <span className="text-[9px] text-slate-400 truncate block">
                {savingsAssets > 0 
                  ? `Disp: ${formatCurrency(liquidAssets)} | Ahorro: ${formatCurrency(savingsAssets)}`
                  : 'Bancos y efectivo'}
              </span>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm p-2.5 rounded-xl border border-slate-700/40">
              <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400 uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
                Deudas Totales
              </div>
              <p className="text-sm font-black text-rose-400 mt-0.5 truncate">
                {hideCardBalance ? '••••••' : `-${formatCurrency(totalDebt)}`}
              </p>
              <span className="text-[9px] text-slate-400 truncate block">
                {creditCardDebt > 0 && loanDebt > 0 
                  ? (hideCardBalance ? 'TC y Préstamos' : `TC: ${formatCurrency(creditCardDebt)}`)
                  : (creditCardDebt > 0 ? 'Tarjetas crédito' : 'Préstamos')}
              </span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default MobileBalanceCard;
